<script setup lang="ts">
import { UiButton } from "@packages/component-library";
import {
  LandingBannerCategories,
  LandingBannerCoupons,
} from "@/modules/landing-module";
import { getSystemCountry } from "@/package/global-helpers/system-country";
import { useI18n } from "vue-i18n";

const { t } = useI18n();

const goToApps = () => {
  document.getElementById("apps").scrollIntoView({
    behavior: "smooth",
  });
};
</script>

<template>
  <div
    class="landing-banner container"
    :class="`landing-banner_${getSystemCountry()}`"
  >
    <div class="landing-banner__left">
      <section class="landing-banner__app">
        <h1>{{ t("home.banner.title") }}</h1>
        <p>{{ t("home.banner.subTitle") }}</p>
        <UiButton
          size="l"
          :label="t('home.banner.button')"
          data-test-id="landing-button-apps"
          @click="goToApps"
        />
      </section>
      <LandingBannerCategories />
    </div>
    <div v-if="getSystemCountry() !== 'md'" class="landing-banner__right">
      <LandingBannerCoupons />

      <RouterLink
        :to="{ name: 'OffersView' }"
        class="landing-banner__offers banner-offers"
      >
        <div class="banner-offers__image">
          <img src="@/assets/images/landing/landing-offers.svg" alt="" />
        </div>
        <div class="banner-offers__info">
          <h5>{{ t("home.offers.title") }}</h5>
          <p>{{ t("home.offers.subTitle") }}</p>
        </div>
      </RouterLink>
    </div>
  </div>
</template>

<style scoped lang="scss">
.landing-banner {
  display: grid;
  grid-template-columns: 822px 1fr;
  gap: calc($base-space * 8);
  align-items: start;

  @media (max-width: 1279px) {
    grid-template-columns: 1fr;
    gap: calc($base-space * 6);
  }

  &_md {
    grid-template-columns: 1fr;
  }

  &__left,
  &__right {
    display: grid;
    grid-template-columns: 1fr;
    gap: calc($base-space * 8);
    align-items: start;

    @media (max-width: 1279px) {
      gap: calc($base-space * 6);
    }
  }

  &__right {
    @media (max-width: 1279px) {
      display: none;
    }
  }

  &__app {
    padding: calc($base-space * 10);
    border-radius: 24px;
    background-color: var(--color-non-semantic-background-soft-yellow);

    @media (max-width: 1279px) {
      padding: calc($base-space * 8);
    }

    @media (max-width: 767px) {
      padding: calc($base-space * 6);
    }

    > h1 {
      @include h2();
      color: var(--color-semantic-content-normal-primary);

      @media (max-width: 1279px) {
        @include h1();
      }
    }

    > p {
      margin: calc($base-space * 6) 0 0;
      @include subtitle();
      color: var(--color-semantic-content-normal-secondary);

      @media (max-width: 1279px) {
        margin: calc($base-space * 4) 0 0;
      }
    }

    > button {
      margin: calc($base-space * 35) 0 0;

      @media (max-width: 1279px) {
        margin: calc($base-space * 25) 0 0;
      }

      @media (max-width: 767px) {
        margin: calc($base-space * 16) 0 0;
        width: 100%;
      }
    }
  }
}

.banner-offers {
  border-radius: 24px;
  background-color: var(--color-semantic-background-normal-bg-secondary);

  &__slider {
    padding: calc($base-space * 8) 0;
  }

  &__image {
    padding: calc($base-space * 15.5) calc($base-space * 15.5)
      calc($base-space * 11.5);
    display: flex;
    align-items: flex-end;
    justify-content: flex-end;
  }

  &__info {
    padding: 0 calc($base-space * 6) calc($base-space * 6);

    > h5 {
      @include body-bold-l();
      color: var(--color-semantic-content-normal-primary);
    }

    > p {
      margin: calc($base-space * 2) 0 0;
      @include body-s();
      color: var(--color-semantic-content-normal-secondary);
    }
  }
}
</style>
